<template>
  <div class="owc-hotspot-sidepanel-item" :data-is-in-modal="mobile">
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div class="owc-hotspot-sidepanel-item__base">
      <div v-if="hasMedia" class="owc-hotspot-sidepanel-item__media" ref="sidepanelMedia">
        <VideoPlayer
          v-if="isVideo"
          class="owc-hotspot-sidepanel-item__mediaComponent"
          :datajson="JSON.stringify(mediaData)"
        ></VideoPlayer>
        <GenericImage
          v-else
          class="owc-hotspot-sidepanel-item__mediaComponent"
          :imageData="mediaData"
        ></GenericImage>
      </div>
      <div
        :class="[
          'owc-hotspot-sidepanel-item__content',
          {
            'owc-hotspot-sidepanel-item__content--extended': !hasMedia,
          },
        ]"
      >
        <h2
          ref="headline"
          class="owc-hotspot-sidepanel-item__title wb-type-heading-s"
          v-html="item && item.headline"
        ></h2>
        <div
          class="owc-hotspot-sidepanel-item__text wb-type-copy owc-rte-stylings"
          v-html="item && item.text"
        ></div>
        <div v-if="link" class="owc-hotspot-sidepanel-item__button">
          <a
            ref="button"
            data-track="false"
            :href="link.url"
            :target="link.target"
            :class="[
              'owc-hotspot-sidepanel-item__button-element',
              {
                'wb-link wb-link--standalone': link.type === 'link',
                'wb-button wb-button--primary wb-button--medium': link.type === 'primary',
                'wb-button wb-button--secondary wb-button--medium': link.type === 'secondary',
              },
            ]"
            @click="customButtonTrackingTrigger"
          >
            {{ link.label }}
          </a>
        </div>
      </div>
      <div
        v-if="footnotesDataJson"
        class="owc-hotspot-sidepanel-item__footnotes"
        v-test-id.ignore="'footnotes'"
      >
        <globalelem-footnote-component :module-id="moduleId" theme="light"></globalelem-footnote-component>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import GenericImage from '@owc-packages/GenericImage/src/GenericImage.vue';
import VideoPlayer from '@owc-packages/VideoPlayer/src/VideoPlayer.vue';
import {
  HotspotSidepanelItemComponent,
  HotspotSidepanelItemComputed,
  HotspotSidepanelItemData,
  HotspotSidepanelItemMethods,
  HotspotSidepanelItemProps,
} from '@owc-packages/Hotspot/src/HotspotSidepanelItem.d';
import { getFootnotesIfAvailable } from '@owc-utils/Footnotes/FootnotesUtils';
import { TestId } from '@owc-utils/TestIdDirective';
import { transformToUnifiedMediaFormat } from './HotspotUtils';
import { TrackingInstance } from '@owc-packages/Tracking/src/Tracking.d';
import { CUSTOM_EVENTS as VIDEO_EVENTS } from '@owc-packages/VideoPlayer/src/VideoPlayer.config';
import initializeTracking, { formatTrackingString } from '@owc-packages/Tracking/src/TrackingUtils';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { isAemEditMode } from '@owc-utils/AEM';
import { mountFootnotes, setModuleId } from '@owc-utils/Footnotes/FootnotesHelper';
let trackingInstance: TrackingInstance | null = null;

/**
 * TODO: Documentation
 * Hotspot Sidepanel
 *
 * A Sidepanel to be placed in the HotspotSimpleItem.
 */
export default (Vue as HotspotSidepanelItemComponent).extend<
  HotspotSidepanelItemData,
  HotspotSidepanelItemMethods,
  HotspotSidepanelItemComputed,
  HotspotSidepanelItemProps
>({
  name: 'owc-hotspot-sidepanel-item',
  props: {
    item: {
      type: Object,
      required: true,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    wbSlideEvent: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    GenericImage,
    VideoPlayer,
    ValidationBoxWrapper,
  },
  data: () => ({
    isCurrentVideoPlaying: false,
    currentVideoPlayButton: null,
  }),
  directives: {
    TestId,
  },
  async mounted() {
    trackingInstance = await initializeTracking();
    if (!this.mobile) {
      this.$nextTick(() => {
        if (this.isVideo) {
          this.$el.addEventListener(VIDEO_EVENTS.ready, this.autoPlayCurrentVideo);
        }
      });
    }
    this.$el.addEventListener(VIDEO_EVENTS.start, () => {
      this.isCurrentVideoPlaying = true;
    });
    this.$el.addEventListener(VIDEO_EVENTS.stop, () => {
      this.isCurrentVideoPlaying = false;
    });
    if (this.wbSlideEvent) {
      this.stopVideo();
    }
    this.$nextTick(() => {
      if (this.$parent && this.$parent.$parent && this.$parent.$el instanceof HTMLElement) {
        mountFootnotes(this.$parent && this.$parent.$parent);
      }
    });
  },
  computed: {
    mediaData() {
      return transformToUnifiedMediaFormat(this.item.media);
    },
    hasMedia() {
      return Boolean(this.item.media?.image);
    },
    isVideo() {
      return Boolean(this.item.media?.video);
    },
    link() {
      return this.item.link;
    },
    isEditMode() {
      return isAemEditMode(this.$parent!);
    },
    footnotesDataJson() {
      return getFootnotesIfAvailable({ footnotes: this.item.footnotes })?.footnotes;
    },
    moduleId() {
      return setModuleId(this.item.component);
    },
  },
  methods: {
    customButtonTrackingTrigger() {
      if (trackingInstance && this.item && this.link) {
        const headline = this.$refs.headline.textContent || '';
        trackingInstance.customTrackingObjectUpdate(
          {
            label: `cta.${formatTrackingString(this.link.label)}.${formatTrackingString(headline)}`,
            category: 'link',
          },
          'click',
          this.$refs.button,
        );
      }
    },
    getCurrentVideo() {
      const wrapper = this.$refs.sidepanelMedia as Element;
      if (this.isVideo) {
        const playButton = wrapper.querySelector(
          '.owc-video-player__play-button, .plyr--playing [data-plyr="play"]',
        ) as HTMLButtonElement;
        if (playButton) {
          this.currentVideoPlayButton = playButton;
        }
      }
    },
    autoPlayCurrentVideo() {
      this.getCurrentVideo();
      this.currentVideoPlayButton?.click();
    },
    stopVideo() {
      this.getCurrentVideo();
      this.currentVideoPlayButton?.click();
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/z-index-list';
@import '@owc-styles/utils/rte';

$moduleName: 'owc-hotspot-sidepanel-item';

.#{$moduleName} {
  @include breakpoint-from(mq3) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;

    width: 100%;
  }

  &__base {
    padding-bottom: var(--wb-spacing-xl);

    @include breakpoint-from(mq3) {
      height: 100%;
      padding-bottom: var(--wb-spacing-xxl);
      overflow-y: auto;
    }
  }
  &__image,
  &__media {
    width: 100%;
  }
  &__content {
    width: 100%;

    padding: var(--wb-spacing-s) var(--wb-spacing-m) var(--wb-spacing-m);

    &--extended {
      padding-top: var(--wb-spacing-m);
    }
  }
  &__text {
    margin-bottom: var(--wb-spacing-m);

    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  &__title {
    margin-bottom: var(--wb-spacing-xs);
  }
  &__button-element {
    width: 100%;

    @include breakpoint-from(mq3) {
      width: auto;
    }
  }
  &__footnotes {
    padding: 0 var(--wb-spacing-m) var(--wb-spacing-s);
  }
}
</style>
